/* Scoped collection enhancement. Native GET filters, pagination and cart forms remain usable without JS. */
(() => {
  if (customElements.get('dh-collection')) return;
  const mobile = () => window.matchMedia('(max-width: 989px)').matches;
  const focusable = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),summary';
  const safeURL = (value) => {
    const url = new URL(value, location.href);
    if (url.origin !== location.origin) throw new Error('Neveljavna povezava.');
    return url;
  };
  class DHCollection extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.classList.add('is-enhanced');
      this.pageLoads = [];
      this.sequence = 0;
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.addEventListener('submit', e => {
        if (!e.target.matches('[data-dhc-filters]')) return;
        e.preventDefault();
        if (!e.target.reportValidity()) return;
        this.load(this.filterURL(e.target), false);
      }, { signal });
      this.addEventListener('change', e => {
        if (e.target.matches('[data-dhc-sort]')) this.load(this.filterURL(e.target.form), false);
      }, { signal });
      this.addEventListener('click', e => {
        const close = e.target.closest('[data-dhc-close]');
        if (close) { this.closeFilters(); return; }
        const link = e.target.closest('a[data-dhc-more],a[data-dhc-update]');
        if (link && !e.ctrlKey && !e.metaKey && !e.shiftKey && e.button === 0) {
          e.preventDefault();
          this.load(link.href, link.hasAttribute('data-dhc-more'));
        }
        if (e.target.closest('[data-dhc-product-link]')) this.savePosition();
      }, { signal });
      this.addEventListener('toggle', e => {
        if (!e.target.matches('.dhc__filter') || !e.target.open) return;
        for (const other of this.querySelectorAll('.dhc__filter[open]')) if (other !== e.target) { other.open = false; other.querySelector('[role=dialog]')?.removeAttribute('aria-modal'); }
        if (mobile()) { e.target.querySelector('[role=dialog]').setAttribute('aria-modal', 'true'); e.target.querySelector('[data-dhc-close]')?.focus(); }
      }, { capture: true, signal });
      this.addEventListener('keydown', e => {
        const detail = this.querySelector('.dhc__filter[open]');
        if (!detail) return;
        if (e.key === 'Escape') { e.preventDefault(); this.closeFilters(); }
        if (e.key === 'Tab' && mobile()) {
          const items = [...detail.querySelector('.dhc__filter-panel').querySelectorAll(focusable)].filter(el => el.getClientRects().length);
          const first = items[0], last = items.at(-1);
          if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last?.focus(); }
          else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first?.focus(); }
        }
      }, { signal });
      document.addEventListener('click', e => {
        if (!e.target.closest('.dhc__filter')) this.closeFilters(false);
      }, { signal });
      window.addEventListener('popstate', () => location.reload(), { signal });
      const categories = this.querySelector('.dhc__categories');
      if (categories) {
        categories.open = !mobile();
        const mql = window.matchMedia('(max-width: 989px)');
        mql.addEventListener('change', () => { categories.open = !mobile(); this.closeFilters(false); }, { signal });
      }
      this.restorePosition();
    }
    disconnectedCallback() { this.controller?.abort(); this.request?.abort(); this.ready = false; }
    closeFilters(focus = true) {
      for (const d of this.querySelectorAll('.dhc__filter[open]')) { d.open = false; d.querySelector('[role=dialog]')?.removeAttribute('aria-modal'); if (focus) d.querySelector('summary').focus(); }
    }
    filterURL(form) {
      const url = safeURL(form.action);
      // Preserve preview/localization parameters, not stale page numbers or filters.
      for (const [key, value] of new URLSearchParams(location.search)) if (!key.startsWith('filter.') && !['sort_by', 'page', 'section_id', 'sections'].includes(key)) url.searchParams.append(key, value);
      for (const [key, value] of new FormData(form)) if (String(value).trim()) url.searchParams.append(key, String(value));
      return url;
    }
    async load(value, append, restoring = false) {
      const url = safeURL(value);
      this.request?.abort();
      this.request = new AbortController();
      const sequence = ++this.sequence;
      const fetchURL = new URL(url);
      fetchURL.searchParams.set('section_id', this.dataset.section);
      const current = this.querySelector('[data-dhc-results]');
      const error = this.querySelector('[data-dhc-error]');
      error.hidden = true;
      current.setAttribute('aria-busy', 'true');
      this.querySelector('[data-dhc-status]').textContent = 'Nalaganje izdelkov …';
      try {
        const response = await fetch(fetchURL, { signal: this.request.signal, headers: { Accept: 'text/html' } });
        if (!response.ok) throw new Error('Izdelkov ni bilo mogoče naložiti. Poskusite znova.');
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const incoming = doc.querySelector(`dh-collection[id="${CSS.escape(this.id)}"] [data-dhc-results]`);
        if (!incoming) throw new Error('Prikaza ni bilo mogoče osvežiti. Osvežite stran.');
        if (sequence !== this.sequence) return false;
        if (append) {
          const grid = current.querySelector('[data-dhc-grid]');
          const existing = new Set([...grid.querySelectorAll('[data-product-id]')].map(el => el.dataset.productId));
          const cards = [...incoming.querySelectorAll('[data-dhc-item]')].filter(el => !existing.has(el.dataset.productId));
          if (!cards.length) throw new Error('Ni novih izdelkov. Osvežite stran za najnovejši seznam.');
          grid.append(...cards);
          current.querySelector('[data-dhc-pagination]').replaceWith(incoming.querySelector('[data-dhc-pagination]'));
          current.querySelector('[data-dhc-visible]').textContent = String(grid.querySelectorAll('[data-dhc-item]').length);
          this.pageLoads.push(url.href);
          if (!restoring) cards[0].querySelector('a')?.focus({ preventScroll: true });
        } else {
          this.closeFilters(false);
          current.replaceWith(incoming);
          this.pageLoads = [];
          history.pushState({ dhCollection: true }, '', url);
          this.querySelector('[data-dhc-count]')?.focus({ preventScroll: true });
        }
        this.querySelector('[data-dhc-status]').textContent = this.querySelector('[data-dhc-count]').textContent;
        return true;
      } catch (err) {
        if (err.name === 'AbortError') return false;
        error.textContent = err.message || 'Povezava ni uspela. Poskusite znova.';
        error.hidden = false;
        this.querySelector('[data-dhc-status]').textContent = 'Nalaganje ni uspelo.';
        return false;
      } finally {
        if (sequence === this.sequence) this.querySelector('[data-dhc-results]')?.removeAttribute('aria-busy');
      }
    }
    positionKey() { return `dhc-position:${this.dataset.section}:${location.pathname}${location.search}`; }
    savePosition() {
      try {
        const variants = [...this.querySelectorAll('dh-collection-card')].map(card => [card.dataset.productUrl, card.querySelector('[data-dhc-variant]')?.value]);
        sessionStorage.setItem(this.positionKey(), JSON.stringify({ pages: this.pageLoads, y: scrollY, variants, time: Date.now() }));
      } catch (_) { /* Storage can be disabled. Navigation must continue. */ }
    }
    async restorePosition() {
      try {
        const key = this.positionKey(), state = JSON.parse(sessionStorage.getItem(key) || 'null');
        if (!state || Date.now() - state.time > 1800000) return;
        sessionStorage.removeItem(key);
        if (!Array.isArray(state.pages) || state.pages.length > 100) return;
        for (const page of state.pages) {
          const url = safeURL(page);
          if (url.pathname !== location.pathname || !await this.load(url, true, true)) break;
        }
        for (const [productURL, id] of state.variants || []) {
          const card = [...this.querySelectorAll('dh-collection-card')].find(el => el.dataset.productUrl === productURL);
          const select = card?.querySelector('[data-dhc-variant]');
          if (select && [...select.options].some(o => o.value === id)) { select.value = id; card.update(); }
        }
        requestAnimationFrame(() => window.scrollTo({ top: Number(state.y) || 0, behavior: 'instant' }));
      } catch (_) { /* A stale saved position never blocks the listing. */ }
    }
  }
  class DHCollectionCard extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.select = this.querySelector('[data-dhc-variant]');
      if (!this.select) return;
      this.classList.add('is-enhanced');
      this.addEventListener('change', e => { if (e.target === this.select) this.update(); });
      this.addEventListener('click', e => {
        const button = e.target.closest('[data-dhc-shade]');
        if (!button || this.busy) return;
        const index = Number(button.dataset.optionIndex);
        const selected = JSON.parse(this.select.selectedOptions[0].dataset.options);
        const candidates = [...this.select.options].filter(o => JSON.parse(o.dataset.options)[index] === button.dataset.dhcShade);
        const candidate = candidates.find(o => JSON.parse(o.dataset.options).every((v, i) => i === index || v === selected[i])) || candidates.find(o => o.dataset.available === 'true') || candidates[0];
        if (candidate) { this.select.value = candidate.value; this.update(); }
      });
      this.querySelector('[data-dhc-cart]').addEventListener('submit', e => { e.preventDefault(); this.addToCart(e.target); });
    }
    update() {
      const option = this.select.selectedOptions[0];
      if (!option) return;
      const options = JSON.parse(option.dataset.options);
      this.querySelectorAll('[data-dhc-shade]').forEach(b => b.setAttribute('aria-pressed', String(options[Number(b.dataset.optionIndex)] === b.dataset.dhcShade)));
      const image = this.querySelector('.dhc-card__image');
      if (image && option.dataset.image) { image.src = option.dataset.image; image.removeAttribute('srcset'); this.classList.add('has-variant-image'); }
      this.querySelectorAll('[data-dhc-product-link]').forEach(a => {
        const url = safeURL(this.dataset.productUrl); url.searchParams.set('variant', option.value); a.href = url;
      });
      this.querySelector('[data-dhc-unit]').textContent = option.dataset.unit || '';
      const variantLabel = this.querySelector('[data-dhc-variant-label]');
      if (variantLabel) variantLabel.textContent = option.dataset.label || '';
      const price = this.querySelector('[data-dhc-price]');
      if (price) price.textContent = option.dataset.price || '';
      this.querySelector('[name=quantity]').value = option.dataset.min || '1';
      const button = this.querySelector('[type=submit]');
      button.disabled = option.dataset.available !== 'true';
      this.updateButton(button, button.disabled ? 'Ni na zalogi' : 'Dodaj v košarico');
      this.querySelector('[data-dhc-cart-message]').hidden = true;
    }
    updateButton(button, label) {
      this.querySelector('[data-dhc-add-label]').textContent = label;
      button.title = label;
      button.setAttribute('aria-label', `${label}: ${button.dataset.productTitle || ''}`.replace(/: $/, ''));
    }
    async addToCart(form) {
      const button = form.querySelector('[type=submit]');
      if (this.busy || button.disabled) return;
      this.busy = true;
      button.disabled = true;
      const data = new FormData(form); // Capture chosen ID before disabling selection.
      this.select.disabled = true;
      const message = this.querySelector('[data-dhc-cart-message]');
      message.hidden = true;
      this.updateButton(button, 'Dodajanje …');
      button.setAttribute('aria-busy', 'true');
      const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
      if (cart?.getSectionsToRender) {
        data.set('sections', cart.getSectionsToRender().map(s => s.id).join(','));
        data.set('sections_url', location.pathname);
        cart.setActiveElement?.(button);
      }
      let added = false;
      try {
        const url = safeURL(form.action); url.pathname = url.pathname.replace(/\/$/, '') + '.js';
        const response = await fetch(url, { method: 'POST', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: data });
        const result = await response.json();
        if (!response.ok || result.status) throw new Error(typeof result.description === 'string' ? result.description : 'Izdelka ni mogoče dodati. Preverite razpoložljivost.');
        added = true;
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') publish(PUB_SUB_EVENTS.cartUpdate, { source: 'dh-collection', productVariantId: data.get('id'), cartData: result });
        if (cart?.renderContents && result.sections && Object.values(result.sections).every(Boolean)) {
          cart.classList.remove('is-empty');
          cart.renderContents(result);
        } else { location.assign(window.routes?.cart_url || '/cart'); }
        message.textContent = 'Izdelek je dodan v košarico.';
        message.hidden = false;
      } catch (err) {
        // Never retry a POST automatically: the server may already have added the item.
        message.textContent = added ? 'Izdelek je dodan. Za pregled odprite košarico.' : (err instanceof TypeError ? 'Povezava se je prekinila. Pred ponovnim dodajanjem preverite košarico.' : err.message);
        message.hidden = false;
      } finally {
        this.busy = false;
        this.select.disabled = false;
        button.disabled = this.select.selectedOptions[0].dataset.available !== 'true';
        button.removeAttribute('aria-busy');
        this.updateButton(button, button.disabled ? 'Ni na zalogi' : 'Dodaj v košarico');
      }
    }
  }
  customElements.define('dh-collection-card', DHCollectionCard);
  customElements.define('dh-collection', DHCollection);
})();
